import { format } from 'date-fns/format';
import { forwardRef, useRef, useState, useCallback, useMemo, useEffect } from 'react';
import { IconNames } from '@shipengine/giger-theme';
import { useToggle } from 'react-use';
import { useMergeRefs } from 'use-callback-ref';
import { ButtonSize, ButtonVariant } from '../../../utils/buttonStyles.js';
import { useKeyPress } from '../../../utils/hooks/useKeyPress.js';
import { Typography } from '../../Typography/Typography.js';
import { getIconDatePickerStyles, getPlaceholderStyles, getTypographyDatePickerStyles, getRightContentDatePickerStyles, getDropdownCalendarStyles, getFooterDatePickerStyles } from './DatePickerStyles.js';
import { jsx, jsxs, Fragment } from '@emotion/react/jsx-runtime';
import { isDatePickerInput, isClearButton, DATE_PICKER_INPUT_LABEL, getDateLocaleFromLocale } from './utils/utils.js';
import { IconTooltip } from '../../IconTooltip/IconTooltip.js';
import { FieldContainer } from '../Field/FieldContainer/FieldContainer.js';
import { FieldContent } from '../Field/FieldContent/FieldContent.js';
import { FieldLabel } from '../Field/FieldLabel/FieldLabel.js';
import { FieldSideContent } from '../Field/FieldSideContent/FieldSideContent.js';
import { Calendar } from '../../Calendar/Calendar.js';
import { DefaultLabelValues } from '../../Calendar/types/labels.js';
import { IconSize } from '../../Icon/types/size.js';
import { ButtonIcon } from '../../ButtonIcon/ButtonIcon.js';
import { Dropdown } from '../../Dropdown/Dropdown.js';
import { Button } from '../../Button/Button.js';

const DEFAULT_FORMAT = 'dd/MM/yyyy';
function DatePickerForwarded(_ref, ref) {
  let {
    disabled = false,
    isClearable = false,
    isInvalid = false,
    label,
    labels,
    locale,
    monthsShown = 1,
    name,
    open = false,
    readOnly = false,
    selected,
    selectsRange,
    dateFormat,
    withBorder = true,
    css,
    placeholderText,
    className,
    formatInputValue: formatInputValueProps,
    dropDownOverflowPadding,
    onChange,
    onClickConfirm,
    onClickCancel,
    ...rest
  } = _ref;
  const newRef = useRef(null);
  const [isDropdownOpen, toggle] = useToggle(open);
  const inputRef = useMergeRefs([newRef, ref]);
  const [selectedLocale, setSelectedLocale] = useState();
  const [initialStartDate, initialEndDate] = Array.isArray(selected) ? selected : [selected, undefined];
  const [selectedDate, setSelectedDate] = useState(initialStartDate);
  const [startDateSelected, setStartDateSelected] = useState(initialStartDate);
  const [finalStartDate, setFinalStartDate] = useState(initialStartDate);
  const [endDateSelected, setEndDateSelected] = useState(initialEndDate);
  const [finalEndDate, setFinalEndDate] = useState(initialEndDate);
  const formatInputValue = useCallback(value => {
    const inputFormat = typeof dateFormat === 'object' ? dateFormat[0] : dateFormat;
    return format(value, inputFormat || DEFAULT_FORMAT, {
      locale: selectedLocale
    });
  }, [dateFormat, selectedLocale]);
  const formatDate = formatInputValueProps !== null && formatInputValueProps !== void 0 ? formatInputValueProps : formatInputValue;
  const inputValue = useMemo(() => {
    if (selectsRange && startDateSelected && endDateSelected) {
      const formatedStartDate = formatDate(startDateSelected);
      const formatedEndDate = formatDate(endDateSelected);
      return `${formatedStartDate} - ${formatedEndDate}`;
    } else if (startDateSelected) {
      return formatDate(startDateSelected);
    }
    return '';
  }, [endDateSelected, formatDate, selectsRange, startDateSelected]);
  const handleOnClick = useCallback(() => {
    !disabled && !readOnly && toggle();
  }, [disabled, readOnly, toggle]);
  const handleCalendarChange = useCallback(date => {
    onChange === null || onChange === void 0 ? void 0 : onChange(date);
    if (selectsRange) {
      const [start, end] = date;
      setStartDateSelected(start);
      setEndDateSelected(end);
    } else {
      setSelectedDate(date);
    }
  }, [onChange, selectsRange]);
  const handleDropdownInnerClick = event => {
    event.stopPropagation();
    event.preventDefault();
  };
  const handleClickAway = event => {
    var _inputRef$current;
    if ((_inputRef$current = inputRef.current) !== null && _inputRef$current !== void 0 && _inputRef$current.contains(event.target)) {
      return;
    }
    onClickCancelHandler();
  };
  const handleOnClear = useCallback(event => {
    event === null || event === void 0 ? void 0 : event.stopPropagation();
    setStartDateSelected(null);
    if (selectsRange) {
      setEndDateSelected(null);
      setFinalStartDate(null);
      setFinalEndDate(null);
    } else {
      setSelectedDate(null);
    }
  }, [selectsRange]);
  const {
    handleKeyPress
  } = useKeyPress({
    onEnter: event => {
      const target = event.target;
      if (isDatePickerInput(target)) {
        handleOnClick();
      } else if (isClearButton(target, (labels === null || labels === void 0 ? void 0 : labels.clearButtonTooltip) || DefaultLabelValues.CLEAR_DATE)) {
        handleOnClear();
      }
    },
    onEscape: event => {
      if (isDropdownOpen) {
        event.stopPropagation();
        onClickCancelHandler();
      }
    },
    onArrowDown: event => {
      const target = event.target;
      if (isDatePickerInput(target) && !isDropdownOpen) {
        handleOnClick();
      } else {
        event.stopPropagation();
      }
    }
  });
  const onClickCancelHandler = useCallback(() => {
    toggle();
    if (selectsRange) {
      setStartDateSelected(finalStartDate);
      setEndDateSelected(finalEndDate);
    } else {
      setSelectedDate(startDateSelected);
    }
    onClickCancel === null || onClickCancel === void 0 ? void 0 : onClickCancel();
  }, [finalEndDate, finalStartDate, onClickCancel, selectsRange, startDateSelected, toggle]);
  const onClickConfirmHandler = useCallback(() => {
    if (selectsRange && startDateSelected && endDateSelected) {
      setFinalStartDate(startDateSelected);
      setFinalEndDate(endDateSelected);
    } else if (selectedDate) {
      setStartDateSelected(selectedDate);
    }
    toggle();
    onClickConfirm === null || onClickConfirm === void 0 ? void 0 : onClickConfirm(selectsRange ? [startDateSelected, endDateSelected] : selectedDate);
  }, [endDateSelected, onClickConfirm, selectedDate, selectsRange, startDateSelected, toggle]);
  const rightIcon = jsx(IconTooltip, {
    css: getIconDatePickerStyles(disabled, readOnly),
    icon: IconNames.CALENDAR,
    size: IconSize.SIZE_MEDIUM,
    tooltipId: (labels === null || labels === void 0 ? void 0 : labels.selectDateIconTooltip) || DefaultLabelValues.SELECT_DATE,
    placement: "bottom",
    hideTooltip: disabled || readOnly,
    children: (labels === null || labels === void 0 ? void 0 : labels.selectDateIconTooltip) || DefaultLabelValues.SELECT_DATE
  });
  useEffect(() => {
    async function getLocale() {
      if (!locale) return;
      const dateFnLocale = await getDateLocaleFromLocale(locale);
      setSelectedLocale(dateFnLocale);
    }
    getLocale();
  }, [locale]);

  /**
   * Control if the parent application/component update the "selected" value
   */
  useEffect(() => {
    if (!selected) {
      handleOnClear();
    } else {
      const [start, end] = Array.isArray(selected) ? selected : [selected, undefined];
      setSelectedDate(start);
      setStartDateSelected(start);
      setEndDateSelected(end);
    }
  }, [handleOnClear, selected]);
  return jsxs(Fragment, {
    children: [jsxs(FieldContainer, {
      disabled: disabled,
      "aria-label": DATE_PICKER_INPUT_LABEL,
      isFocused: isDropdownOpen,
      isInvalid: isInvalid,
      readOnly: readOnly,
      ref: inputRef,
      tabIndex: 0,
      onKeyDown: handleKeyPress,
      css: css,
      className: className,
      onClick: handleOnClick,
      children: [jsxs(FieldContent, {
        children: [label && jsx(FieldLabel, {
          htmlFor: name,
          hasFocusedValue: isDropdownOpen || !!inputValue,
          isDisabled: disabled,
          children: label
        }), placeholderText && !inputValue && jsx(Typography, {
          variant: "body2",
          css: getPlaceholderStyles,
          children: placeholderText
        }), jsx(Typography, {
          bold: true,
          role: "textbox",
          "aria-label": label,
          variant: "subtitle2",
          css: getTypographyDatePickerStyles(disabled, isInvalid, !!label, readOnly),
          children: inputValue || ''
        }), jsx("input", {
          type: "hidden",
          name: name,
          value: inputValue || ''
        })]
      }), (rightIcon || isClearable) && jsxs(FieldSideContent, {
        align: "right",
        css: getRightContentDatePickerStyles,
        children: [isClearable && inputValue && jsx(ButtonIcon, {
          ariaDescribedby: (labels === null || labels === void 0 ? void 0 : labels.clearButtonTooltip) || DefaultLabelValues.CLEAR_DATE,
          "aria-label": (labels === null || labels === void 0 ? void 0 : labels.clearButtonTooltip) || DefaultLabelValues.CLEAR_DATE,
          disabled: disabled || readOnly,
          icon: IconNames.CANCEL_FILLED,
          size: ButtonSize.SMALL,
          variant: ButtonVariant.TEXT,
          onClick: handleOnClear,
          onKeyDown: event => {
            handleKeyPress(event);
          },
          children: (labels === null || labels === void 0 ? void 0 : labels.clearButtonTooltip) || DefaultLabelValues.CLEAR_DATE
        }), rightIcon]
      })]
    }), jsxs(Dropdown, {
      css: getDropdownCalendarStyles(withBorder),
      isOpen: isDropdownOpen,
      placement: "bottom-end",
      dropdownWidth: monthsShown > 1 ? 'auto' : undefined,
      popoutOverflowPadding: dropDownOverflowPadding,
      reference: inputRef.current,
      onClickAway: handleClickAway,
      onClick: handleDropdownInnerClick,
      children: [jsx(Calendar, {
        endDate: endDateSelected,
        labels: labels,
        locale: selectedLocale,
        monthsShown: monthsShown,
        selectsRange: selectsRange,
        shouldCloseOnSelect: false,
        selected: selectsRange ? startDateSelected : selectedDate,
        startDate: selectsRange ? startDateSelected || finalStartDate : startDateSelected,
        onChange: handleCalendarChange,
        withBorder: false,
        dateFormat: dateFormat,
        ...rest
      }), jsxs("footer", {
        css: getFooterDatePickerStyles,
        children: [jsx(Button, {
          "aria-describedby": (labels === null || labels === void 0 ? void 0 : labels.cancelButton) || DefaultLabelValues.CANCEL,
          variant: ButtonVariant.OUTLINED,
          onClick: onClickCancelHandler,
          children: (labels === null || labels === void 0 ? void 0 : labels.cancelButton) || DefaultLabelValues.CANCEL
        }), jsx(Button, {
          "aria-describedby": (labels === null || labels === void 0 ? void 0 : labels.confirmButton) || DefaultLabelValues.CONFIRM,
          disabled: selectsRange ? !endDateSelected : !selectedDate,
          onClick: onClickConfirmHandler,
          children: (labels === null || labels === void 0 ? void 0 : labels.confirmButton) || DefaultLabelValues.CONFIRM
        })]
      })]
    })]
  });
}
DatePickerForwarded.displayName = 'DatePicker';
const DatePicker = /*#__PURE__*/forwardRef(DatePickerForwarded);

export { DEFAULT_FORMAT, DatePicker };
//# sourceMappingURL=DatePicker.js.map
